import { Dialog, DialogTitle, DialogContent, Box, DialogActions, Button, Grid, Stack, Chip, Tooltip, IconButton } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FileList from './FileList';
import { v4 as uuidv4 } from 'uuid';
import MobileFriendly from '@mui/icons-material/MobileFriendly';
import MobileOffIcon from '@mui/icons-material/MobileOff';
import FileUploadBox from './FileUploadBox';
import FilePreview from './FilePreview';
import { useIssueActivationToken } from 'hooks/devices/useIssueToken';
import QRCode from 'react-qr-code';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LinkedCameraIcon from '@mui/icons-material/LinkedCamera';
import { useDispatch, useSelector } from 'react-redux';
import { ON } from 'enums/signalrConnection';
import { SIGNALR_DEVICE_PAIRED } from 'store/actions';
import { useMobileConnection } from 'context/SignalR/mobileActionsConnection';
import { devices } from 'apis';
import { useMe } from 'hooks/useMe';
import { ActionType } from 'enums/actionType';
import Conditional from 'common/Conditional';
import useSettings from 'hooks/useSettings';

const FileUploadDialog = (props) => {
    const {
        open,
        setOpen,
        bundle,
        onSave,
        onDelete,
        limit = 10,
        avatar = false,
        avatarFile,
        fixedAspectRatio = false,
        aspectRatio,
        showPairStatus = true
    } = props;
    const { t } = useTranslation();
    const paired = useSelector((state) => state.signalr.isDevicePaired);
    const accessToken = useSelector((state) => state.auth.accessToken);
    const dispatch = useDispatch();
    const whoami = useMe();
    const { connection, openQrCode, requestPhoto } = useMobileConnection();
    const { token, triggerRefetch } = useIssueActivationToken();
    const { project_HidePairDevice } = useSettings();

    const [openQrDialog, setOpenQrDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedFileId, setSelectedFileId] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [inPreview, setInPreview] = useState(false);
    const [photoCropped, setPhotoCropped] = useState(false);
    const [filesToDelete, setFilesToDelete] = useState([]);

    const handleClose = () => {
        setInPreview(false);
        setSelectedFileId(null);
        setSelectedFile(null);
        setSelectedFiles([]);
        setPhotoCropped(false);
        setOpen(false);
    };

    const handleSave = () => {
        if (onSave && typeof onSave === 'function')
            onSave(
                selectedFiles.map((file) => ({ id: file.id, file: file.qubFile })),
                filesToDelete
            );
        handleClose();
    };

    // const handleOnDevicePaired = () => {
    //     dispatch({ type: SIGNALR_DEVICE_PAIRED });
    //     setOpenQrDialog(false);
    // };

    // const handleDataReceived = (data, limit, selectedFiles) => {
    //     console.log(limit, selectedFiles.length);
    //     if (limit < selectedFiles.length + 1) {
    //         window.alert(`Only ${limit} file${limit !== 1 ? 's' : ''} can be uploaded at a time`);
    //     } else {
    //         const response = JSON.parse(data);
    //         const fileData = Buffer.from(response?.data, 'base64');
    //         const blob = new Blob([fileData], { type: 'image/png' });
    //         const file = new File([blob], 'test.png', { type: 'image/png' });
    //         setSelectedFiles((prev) => [...prev, { id: uuidv4(), qubFile: file }]);
    //     }
    // };

    //

    const base64ToFile = (base64String, fileName) => {
        const byteCharacters = atob(base64String);
        const arrayBuffer = new ArrayBuffer(byteCharacters.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteCharacters.length; i++) {
            uint8Array[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob([uint8Array], { type: 'image/png' });
        const file = new File([blob], fileName, { type: 'image/png' });
        return file;
    };

    const handlePhotoReceived = (data) => {
        if (selectedFiles.length > 1) {
            window.alert(`Only 1 file can be uploaded at a time`);
            return;
        }
        if (connection) {
            connection.off(ON.actionResponse, handlePhotoReceived);
        }
        if (data) {
            const response = JSON.parse(data);
            const date = new Date();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const file = base64ToFile(response.data, `image${year}${month}${day}.png`);
            setSelectedFiles((prev) => [...prev, { id: uuidv4(), qubFile: file }]);
            // onFilesSelected([file]);
        }
    };

    const handleRequestPhoto = () => {
        if (connection) {
            connection.on(ON.actionResponse, handlePhotoReceived);
            requestPhoto((data) => handlePhotoReceived(data));
        }
    };
    //

    // when files are changed set the selected file to the first file
    useEffect(() => {
        if (selectedFiles.length > 0) {
            setSelectedFileId(selectedFiles[0].id);
        }
    }, [selectedFiles]);

    // useEffect(() => {
    //     if (connection) {
    //         connection.off(ON.actionResponse);
    //         connection.on(ON.devicePaired, handleOnDevicePaired);
    //         connection.on(ON.actionResponse, (data) => handlePhotoReceived(data));
    //     }
    // }, [connection]);

    useEffect(() => {
        if (open) {
            if (avatar && avatarFile) setSelectedFiles([{ id: uuidv4(), qubFile: avatarFile }]);
            else if (bundle?.files) setSelectedFiles(bundle?.files.map((qubFile) => ({ id: uuidv4(), qubFile })));
        }
    }, [open]);

    const handleDeleteFile = async ({ id, qubId }) => {
        if (selectedFileId === id) {
            setInPreview(false);
            setSelectedFileId(null);
            setSelectedFile(null);
        }
        if (onDelete && typeof onDelete === 'function' && qubId) {
            const deleted = await onDelete(qubId);
            if (deleted) {
                setSelectedFiles((prev) => prev.filter((file) => file.id !== id));
            }
        } else if (!qubId) {
            setSelectedFiles((prev) => prev.filter((file) => file.id !== id));
        }
        setPhotoCropped(false);
    };

    const handleUpload = (files) => {
        if (limit && limit < files.length + selectedFiles.length) {
            window.alert(`Only ${limit} file${limit !== 1 ? 's' : ''} can be uploaded at a time`);
        } else {
            setSelectedFiles((prev) => [...prev, ...files.map((qubFile) => ({ id: uuidv4(), qubFile }))]);
        }
    };

    const handleConnect = async () => {
        if (paired) return;
        openQrCode();
        // setOpenQrDialog(true);
    };

    // const handleRequestPhoto = async () => {
    //     if (!paired) return;
    //     await devices
    //         .requestAction({
    //             actionId: uuidv4().toString(),
    //             userId: whoami?.whoami?.userId,
    //             accessToken,
    //             actionType: ActionType.captureImage,
    //             payload: {}
    //         })
    //         .catch((err) => console.log(err));
    // };

    const handleClosePreview = () => {
        setInPreview(false);
        setSelectedFileId(null);
        setSelectedFile(null);
    };

    const handleSaveCrop = (prevFile, croppedFile) => {
        if (prevFile?.qubFile?.id) {
            setFilesToDelete((prev) => [...prev, prevFile?.qubFile?.id]);
        }
        setSelectedFiles((prev) => prev.map((file) => (file.id === prevFile?.id ? { ...file, qubFile: croppedFile } : file)));
        setPhotoCropped(true);
    };

    useEffect(() => {
        if (selectedFileId) {
            setSelectedFile(selectedFiles.find((file) => file.id === selectedFileId));
            setInPreview(true);
        }
    }, [selectedFileId, selectedFiles]);

    return (
        <>
            <Dialog open={open} maxWidth="xl" fullWidth>
                <DialogTitle>{t('UploadFiles')}</DialogTitle>
                <DialogContent sx={{ height: '85vh', overflow: 'hidden' }}>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="space-between" px={1}>
                                        <Conditional condition={!(project_HidePairDevice === 'true')}>
                                            <Stack direction="row" spacing={1}>
                                                {showPairStatus && (
                                                    <Chip
                                                        label={paired ? t('Paired') : t('PairDevice')}
                                                        icon={
                                                            paired ? (
                                                                <MobileFriendly style={{ color: '#fff' }} />
                                                            ) : (
                                                                <MobileOffIcon style={{ color: '#fff' }} />
                                                            )
                                                        }
                                                        sx={{
                                                            background: paired ? '#1a910a' : '#4d79bc',
                                                            color: '#fff',
                                                            boxShadow: paired ? 0 : 3,
                                                            ':hover': { background: paired ? '#1a910a' : '#79AEC9' }
                                                        }}
                                                        onClick={handleConnect}
                                                    />
                                                )}
                                                {paired && (
                                                    <Chip
                                                        label={t('RequestPhoto')}
                                                        icon={<LinkedCameraIcon style={{ color: '#fff' }} />}
                                                        sx={{
                                                            background: '#4d79bc',
                                                            color: '#fff',
                                                            boxShadow: 3,
                                                            ':hover': { background: '#79AEC9' }
                                                        }}
                                                        onClick={handleRequestPhoto}
                                                    />
                                                )}
                                            </Stack>
                                        </Conditional>
                                        {inPreview && (
                                            <Tooltip title="close preview">
                                                <Box boxShadow={2} sx={{ borderRadius: 50, background: '#fff' }}>
                                                    <IconButton sx={{ color: '#b30b00' }} onClick={handleClosePreview}>
                                                        <CloseRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid item container xs={12} sx={{ height: '72vh' }}>
                                    {inPreview ? (
                                        <FilePreview
                                            file={selectedFile}
                                            onSaveCrop={handleSaveCrop}
                                            fixedAspectRatio={fixedAspectRatio}
                                            aspectRatio={aspectRatio}
                                        />
                                    ) : (
                                        <FileUploadBox onUpload={handleUpload} avatar={avatar} />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <FileList
                                selectedFiles={selectedFiles}
                                limit={limit}
                                onDelete={handleDeleteFile}
                                selectedFileId={selectedFileId}
                                setSelectedFileId={setSelectedFileId}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        sx={{ background: '#c42c0e', ':hover': { background: '#c9807d', color: '#fff' } }}
                        onClick={handleClose}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                        disabled={avatar && !photoCropped}
                        onClick={handleSave}
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openQrDialog}>
                <DialogTitle>{t('PairDevice')}</DialogTitle>
                <DialogContent>
                    <Box>
                        <QRCode size={256} value={token ?? ''} viewBox="0 0 256 256" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        sx={{ background: '#4d79bc', ':hover': { background: '#79AEC9', color: '#fff' } }}
                        onClick={triggerRefetch}
                    >
                        {t('NewQR')}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ background: '#c42c0e', ':hover': { background: '#c9807d', color: '#fff' } }}
                        onClick={() => setOpenQrDialog(false)}
                    >
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FileUploadDialog;
